import { supabase } from '../config/supabase'

export const PENDING_RECOVERY_FLAG_KEY = 'pendingPasswordRecoveryActive'
export const PASSWORD_RESET_EMAIL_KEY = 'passwordResetEmail'

let recoveryInitPromise = null

export const markPasswordRecoveryPending = () => {
  sessionStorage.setItem(PENDING_RECOVERY_FLAG_KEY, 'true')
}

export const clearPasswordRecoveryPending = () => {
  sessionStorage.removeItem(PENDING_RECOVERY_FLAG_KEY)
  sessionStorage.removeItem(PASSWORD_RESET_EMAIL_KEY)
}

export const hasPendingPasswordRecovery = () => ({
  flag: sessionStorage.getItem(PENDING_RECOVERY_FLAG_KEY) === 'true',
})

export const isVerifyResetCodePath = (pathname = window.location.pathname) =>
  pathname === '/verify-reset-code'

const cleanRecoveryUrl = () => {
  const url = new URL(window.location.href)
  window.history.replaceState({}, document.title, url.pathname)
}

const mapRecoveryError = (error) => {
  const msg = (error?.message || '').toLowerCase()
  if (
    msg.includes('code verifier') ||
    msg.includes('pkce') ||
    msg.includes('non-empty') ||
    msg.includes('both auth code and code verifier')
  ) {
    return (
      'Please open this reset link in the same browser where you clicked "Forgot Password". ' +
      'If you used a different browser or device, go back, request a new link, and open it here.'
    )
  }
  if (msg.includes('expired') || msg.includes('invalid')) {
    return 'This reset link has expired. Please request a new password reset.'
  }
  return 'Invalid or expired reset link. Please request a new password reset.'
}

const waitForDetectSession = () =>
  new Promise((resolve) => setTimeout(resolve, 200))

/**
 * Establish a recovery session from the URL (PKCE code, token_hash, or hash tokens).
 * Uses a singleton promise so React Strict Mode does not exchange the code twice.
 */
export const establishPasswordRecoverySession = async () => {
  if (recoveryInitPromise) return recoveryInitPromise

  recoveryInitPromise = (async () => {
    await waitForDetectSession()

    const url = new URL(window.location.href)

    // Session may already exist if detectSessionInUrl processed the link
    const { data: { session: existingSession } } = await supabase.auth.getSession()
    if (existingSession?.user) {
      cleanRecoveryUrl()
      return { success: true }
    }

    const tokenHash = url.searchParams.get('token_hash')
    const type = url.searchParams.get('type')
    if (tokenHash && type === 'recovery') {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'recovery',
      })
      if (error) {
        const { data: { session: retry } } = await supabase.auth.getSession()
        if (retry?.user) {
          cleanRecoveryUrl()
          return { success: true }
        }
        return { success: false, error: mapRecoveryError(error) }
      }
      cleanRecoveryUrl()
      return { success: true }
    }

    const code = url.searchParams.get('code')
    if (code) {
      const { error } = await supabase.auth.exchangeCodeForSession(window.location.href)
      if (error) {
        const { data: { session: retry } } = await supabase.auth.getSession()
        if (retry?.user) {
          cleanRecoveryUrl()
          return { success: true }
        }
        return { success: false, error: mapRecoveryError(error) }
      }
      cleanRecoveryUrl()
      return { success: true }
    }

    const hash = window.location.hash
    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1))
      if (hashParams.get('type') === 'recovery') {
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')
        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          })
          if (error) {
            const { data: { session: retry } } = await supabase.auth.getSession()
            if (retry?.user) {
              cleanRecoveryUrl()
              return { success: true }
            }
            return { success: false, error: mapRecoveryError(error) }
          }
        }
        cleanRecoveryUrl()
        return { success: true }
      }
    }

    const { data: { session } } = await supabase.auth.getSession()
    if (session?.user) {
      cleanRecoveryUrl()
      return { success: true }
    }

    return {
      success: false,
      error: 'Invalid or expired reset link. Please request a new password reset.',
    }
  })()

  return recoveryInitPromise
}

export const isResetPasswordPath = (pathname = window.location.pathname) =>
  pathname === '/reset-password' || pathname === '/resetpassword'

export const isRecoveryAuthPath = (pathname = window.location.pathname) =>
  isResetPasswordPath(pathname) || isVerifyResetCodePath(pathname)

const mapOtpError = (error) => {
  const msg = (error?.message || '').toLowerCase()
  if (msg.includes('expired') || msg.includes('invalid') || msg.includes('otp')) {
    return 'Invalid or expired code. Please request a new reset email and try again.'
  }
  return error?.message || 'Could not verify the code. Please try again.'
}

const waitForRecoverySession = async (maxAttempts = 8) => {
  for (let i = 0; i < maxAttempts; i += 1) {
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.user) return session
    await new Promise((resolve) => setTimeout(resolve, 150))
  }
  return null
}

/** OTP recovery — enter the 6-digit code from the reset email in the browser. */
export const verifyRecoveryOtp = async (email, token) => {
  if (!email?.trim() || !token?.trim()) {
    return { success: false, error: 'Email and reset code are required.' }
  }

  const normalizedEmail = email.trim().toLowerCase()
  const normalizedToken = token.trim()

  let { error } = await supabase.auth.verifyOtp({
    email: normalizedEmail,
    token: normalizedToken,
    type: 'recovery',
  })

  if (error) {
    const fallback = await supabase.auth.verifyOtp({
      email: normalizedEmail,
      token: normalizedToken,
      type: 'email',
    })
    error = fallback.error
  }

  if (error) {
    const session = await waitForRecoverySession(2)
    if (session?.user) {
      markPasswordRecoveryPending()
      return { success: true }
    }
    return { success: false, error: mapOtpError(error) }
  }

  const session = await waitForRecoverySession()
  if (!session?.user) {
    return {
      success: false,
      error: 'Could not start a reset session. Please request a new code and try again.',
    }
  }

  markPasswordRecoveryPending()
  return { success: true }
}

export const hasActiveRecoverySession = async () => {
  const pending = hasPendingPasswordRecovery()
  if (!pending.flag) return false
  const { data: { session } } = await supabase.auth.getSession()
  return Boolean(session?.user)
}
